import { useState } from "react";
import {
  addDoc,
  collection,
  doc,
  updateDoc,
  serverTimestamp,
  getDoc,
} from "firebase/firestore";
import { db } from "../../firebase";

export default function PackagePaymentModal({
  activePackageDoc,
  onClose,
  onSuccess,
}) {
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!activePackageDoc) return null;

  const total = Number(activePackageDoc.total_amount || 0);
  const alreadyPaid = Number(activePackageDoc.paid_amount || 0);
  const currentDue = Math.max(total - alreadyPaid, 0);
  const [paymentMode, setPaymentMode] = useState("");
  const handleAddPayment = async () => {
    setError("");

    const payAmount = Number(amount);

    if (!paymentMode) {
  setError("Please select payment mode");
  return;
}

    // 🔴 Basic validation
    if (!amount || payAmount <= 0) {
      setError("Please enter a valid payment amount");
      return;
    }

    setLoading(true);

    try {
      // 🔥 STEP 1: Always fetch latest package data (VERY IMPORTANT)
      const packageRef = doc(db, "packages", activePackageDoc.id);
      const packageSnap = await getDoc(packageRef);

      if (!packageSnap.exists()) {
        setError("Package not found");
        setLoading(false);
        return;
      }

      const pkg = packageSnap.data();

      const total = Number(pkg.total_amount || 0);
      const alreadyPaid = Number(pkg.paid_amount || 0);
      const currentDue = Math.max(total - alreadyPaid, 0);
      
      // 🔴 Prevent overpayment (after fresh fetch)
      if (payAmount > currentDue) {
        setError(`Amount cannot exceed due balance (₹ ${currentDue})`);
        setLoading(false);
        return;
      }
      
      // 2️⃣ Add payment receipt (ledger)
      await addDoc(collection(db, "package_payments"), {
        package_id: activePackageDoc.id,
        patient_id: pkg.patient_id,
        vpjc_id: pkg.vpjc_id,
        amount: payAmount,
        payment_mode: paymentMode,
        created_at: serverTimestamp(),
        branch_name: pkg.branch_name,
        note: "Manual Package Payment",
      });

      // 3️⃣ Correct billing calculation (using latest DB values)
      const newPaid = alreadyPaid + payAmount;
      const newDue = Math.max(total - newPaid, 0);

      let newStatus = "PARTIAL";
      if (newDue === 0) {
        newStatus = "PAID";
      }

      // 4️⃣ Update package billing summary (MAIN SOURCE OF TRUTH)
      await updateDoc(packageRef, {
        paid_amount: newPaid,
        due_amount: newDue,
        consultation_payment_status: newStatus,
        payment_mode: paymentMode,
        updated_at: serverTimestamp(),
      });

      // 5️⃣ Refresh patient page
      if (onSuccess) {
        await onSuccess();
      }

      onClose();
    } catch (err) {
      console.error("Package payment error:", err);
      setError("Failed to save payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-[95%] max-w-md shadow-lg">
        {/* Header */}
        <h3 className="text-lg font-semibold text-[#204080] mb-4">
          Add Package Payment
        </h3>

        {/* 🔎 Package Summary (VERY USEFUL FOR ADMIN) */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm space-y-1">
          <p>
            <span className="font-semibold">Total Package:</span> ₹ {total}
          </p>
          <p className="text-green-600">
            <span className="font-semibold">Paid Till Now:</span> ₹{" "}
            {alreadyPaid}
          </p>
          <p className="text-red-600">
            <span className="font-semibold">Due Balance:</span> ₹ {currentDue}
          </p>
        </div>

        {/* Amount Input */}
        <label className="text-sm font-semibold text-gray-600">
          Amount Received (₹)
        </label>
        <input
          type="number"
          placeholder="Enter amount received"
          value={amount}
          onChange={(e) => {
            setAmount(e.target.value);
            setError("");
          }}
          onWheel={(e) => e.target.blur()}
          className="w-full border rounded-lg px-3 py-2 mt-1 mb-3"
        />

        {/* Error Message */}
        {error && (
          <p className="text-sm text-red-600 mb-3 font-medium">{error}</p>
        )}

        <label className="text-sm font-semibold text-gray-600">
  Payment Mode
</label>

<select
  value={paymentMode}
  onChange={(e) => {
    setPaymentMode(e.target.value);
    setError("");
  }}
  className={`w-full border rounded-lg px-3 py-2 mt-1 mb-3
    ${
      paymentMode === "ONLINE"
        ? "text-blue-700 border-blue-300"
        : paymentMode === "CASH"
          ? "text-green-700 border-green-300"
          : ""
    }`}
>
  <option value="">Select Payment Mode</option>
  <option value="CASH">Cash</option>
  <option value="ONLINE">Online</option>
</select>
        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>

          <button
            onClick={handleAddPayment}
            disabled={loading || currentDue === 0}
            className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-60"
          >
            {loading
              ? "Saving..."
              : currentDue === 0
                ? "Fully Paid"
                : "Save Payment"}
          </button>
        </div>
      </div>
    </div>
  );
}
